import React, { useState, useEffect } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Table, Button, Space, Select, DatePicker } from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  REPORT_DATE_FORMAT,
  formatReportDate,
} from "../utils/reportDateRangeUtils";

const { Option } = Select;
const { RangePicker } = DatePicker;

const IndustryWiseReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [dateRange, setDateRange] = useState(null);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [industry, setIndustry] = useState(undefined);

  const appendDuration = (params) => {
    const startDate = formatReportDate(dateRange?.[0]);
    const endDate = formatReportDate(dateRange?.[1]);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
  };

  const beginRequest = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginRequest();
    const fetchIndustryData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("financialYear", financialYear);
        appendDuration(params);
        if (industry && String(industry).trim()) {
          params.set("industry", String(industry).trim());
        }

        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getIndustryWiseReport?${params.toString()}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const result = await response.json();
        if (!isCurrent()) return;

        if (result.success) {
          const rows = result.data?.brandSegments || [];
          setData(rows);

          const industries = Array.isArray(result.data?.industries)
            ? result.data.industries
            : [];
          const opts = industries
            .map((s) => String(s ?? "").trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
            .map((s) => ({ label: s, value: s }));
          setIndustryOptions(opts);
        } else {
          console.error("Failed to fetch industry data:", result.message);
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error("Error fetching industry data:", error);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    fetchIndustryData();
  }, [apiBaseUrl, financialYear, dateRange, industry]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isExcel", "true");
      appendDuration(params);
      if (industry && String(industry).trim()) {
        params.set("industry", String(industry).trim());
      }
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getIndustryWiseReport?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const startDate = formatReportDate(dateRange?.[0]);
        const endDate = formatReportDate(dateRange?.[1]);
        a.download =
          startDate && endDate
            ? `Industry_Wise_Report_${startDate}_to_${endDate}.xlsx`
            : `Industry_Wise_Report_${financialYear}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Failed to download Excel file");
      }
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const columns = [
    {
      title: "Industries",
      dataIndex: "brandSegment",
      key: "brandSegment",
      width: 200,
      align: "center",
      render: (brandSegment) => (
        <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
          {brandSegment || "N/A"}
        </span>
      ),
    },
    {
      title: "Sales",
      dataIndex: "totalSales",
      key: "totalSales",
      width: 150,
      align: "center",
      render: (totalSales) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {totalSales ? formatCurrency(totalSales) : "N/A"}
        </span>
      ),
    },
    {
      title: "%",
      dataIndex: "percentageDistribution",
      key: "percentageDistribution",
      width: 100,
      align: "center",
      render: (percentageDistribution) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {percentageDistribution ? `${percentageDistribution}%` : "N/A"}
        </span>
      ),
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        />
      ),
    },
  };

  // Calculate totals
  const totalSales = data.reduce(
    (sum, item) => sum + (item.totalSales || 0),
    0
  );
  const totalPercentage = data.reduce((sum, item) => {
    const percentage = parseFloat(item.percentageDistribution) || 0;
    return sum + percentage;
  }, 0);

  // Add total row to filtered data
  const dataWithTotal = [
    ...data,
    {
      key: "total",
      brandSegment: "Grand Total",
      totalSales: totalSales,
      percentageDistribution: totalPercentage.toFixed(2),
    },
  ];

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Industry Wise Report
            </Space>
            <Space>
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ width: 120 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format={REPORT_DATE_FORMAT}
                style={{ width: 260 }}
                allowClear
              />
              <Select
                showSearch
                allowClear
                placeholder="Filter by Industry"
                value={industry}
                onChange={(v) => setIndustry(v)}
                options={industryOptions}
                style={{ width: 280 }}
                filterOption={(input, opt) =>
                  String(opt?.label || "")
                    .toLowerCase()
                    .startsWith(String(input || "").toLowerCase())
                }
              />
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
                style={{ marginLeft: "8px" }}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={dataWithTotal}
          loading={loading}
          components={components}
          rowKey={(record) => record.key || record.brandSegment}
          pagination={false}
          sticky
          scroll={{ y:500 }}
          rowClassName={(record) =>
            record.brandSegment === "Grand Total" ? "total-row" : ""
          }
        />
      </Card>
    </div>
  );
};

export default IndustryWiseReport;
